import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import { Bucket, StaticContext, withStyledTheme } from '@at/sauron-platform-core';
import { withAppContext } from '@at/sauron-platform-core';
import { withGraphQL } from '@at/sauron-platform-graphql';
import { withScripts } from './script.js';
import { loadableReady } from './loadable/loadable-ready.js';
import { useApolloClient } from '@apollo/client';
import { HelmetProvider } from 'react-helmet-async';
const SinglePageAppContext = ({ staticContext = {}, children, gqlEnabled = true, }) => {
    if (import.meta.env.SSR && gqlEnabled) {
        const client = useApolloClient();
        staticContext.client = client;
    }
    return React.createElement(StaticContext.Provider, { value: staticContext }, children);
};
const SinglePageApp = ({ children, appName: applicationName, appVersion = 'local', config, mode = 'csr', staticContext = {}, helmetContext = {}, abTestGroups = [], bucket = Bucket.premiummobile, graphQL = { enabled: true }, router = { enabled: true }, styledTheme = { enabled: true }, scripts = { disabledScripts: [] }, }) => {
    const ssrMode = mode === 'ssr';
    let App = () => React.createElement(React.Fragment, null, children);
    App.displayName = 'SinglePageAppWrapper';
    if (!import.meta.env.SSR && router.enabled) {
        App = () => React.createElement(BrowserRouter, null, children);
    }
    const appName = applicationName + (process.env.NODE_ENV === 'development' ? `-${appVersion}` : '');
    const gql = Object.assign({ enabled: true }, graphQL);
    if (!ssrMode) {
        App = withScripts(App, scripts.disabledScripts.map((disabledScript) => disabledScript.script));
    }
    let WrappedApp = () => (React.createElement(SinglePageAppContext, { staticContext: staticContext, gqlEnabled: gql.enabled },
        React.createElement(HelmetProvider, { context: helmetContext },
            React.createElement(App, null))));
    WrappedApp = styledTheme.enabled ? withStyledTheme(WrappedApp, styledTheme.extendedTheme) : WrappedApp;
    if (import.meta.env.SSR && gql.enabled) {
        if (gql.client) {
            WrappedApp = withGraphQL(WrappedApp, {
                client: gql.client,
            });
        }
        else {
            throw new Error('GraphQL client must be provided for SSR mode.');
        }
    }
    else if (gql.enabled) {
        WrappedApp = withGraphQL(WrappedApp, Object.assign(Object.assign({ ssrMode,
            appName, batchingEnabled: gql.batchingEnabled, cache: gql.cache }, (gql.uri && { uri: gql.uri })), { appendOriginator: gql.appendOriginator }));
    }
    WrappedApp = withAppContext(WrappedApp, Object.assign({ appName }, config), ssrMode, abTestGroups, bucket);
    return React.createElement(WrappedApp, null);
};
const bootstrap = (App, options = {}) => {
    const { loadableEnabled = true, render = ReactDOM.hydrate, rootElementId = 'root' } = options;
    if (loadableEnabled) {
        loadableReady(() => {
            render(React.createElement(App, null), document.getElementById(rootElementId));
        });
    }
    else {
        render(React.createElement(App, null), document.getElementById(rootElementId));
    }
};
export { SinglePageApp, bootstrap };
